import { useCallback, useEffect, useRef, useState } from 'react'

function calcPositions(
  elements: Map<string, HTMLElement>,
  containerRect: DOMRect,
): Record<string, number> {
  const positions: Record<string, number> = {}
  elements.forEach((el, id) => {
    const rect = el.getBoundingClientRect()
    positions[id] = rect.top + rect.height / 2 - containerRect.top
  })
  return positions
}

function positionsEqual(
  prev: Record<string, number>,
  next: Record<string, number>,
): boolean {
  const keys = Object.keys(next)
  return (
    keys.length === Object.keys(prev).length &&
    keys.every((k) => prev[k] === next[k])
  )
}

export default function useConnectorPositions() {
  // Layout refs for scroll containers and the connector column
  const invoiceScrollRef = useRef<HTMLDivElement>(null)
  const poScrollRef = useRef<HTMLDivElement>(null)
  const connectorRef = useRef<HTMLDivElement>(null)
  // Wraps everything in the invoice pane, so a change in its height (a related
  // invoice card expanding, say) re-measures the rows below it.
  const invoiceContentRef = useRef<HTMLDivElement>(null)

  // Row element tracking
  const invoiceRowElements = useRef(new Map<string, HTMLTableRowElement>())
  const poRowElements = useRef(new Map<string, HTMLTableRowElement>())
  // Points on the related invoice cards that their connectors start from
  const relatedAnchorElements = useRef(new Map<string, HTMLElement>())

  // Row positions relative to connector column
  const [invoiceRowPositions, setInvoiceRowPositions] = useState<
    Record<string, number>
  >({})
  const [poRowPositions, setPoRowPositions] = useState<Record<string, number>>(
    {},
  )
  const [relatedAnchorPositions, setRelatedAnchorPositions] = useState<
    Record<string, number>
  >({})

  // Row ref callbacks for tracking table row DOM elements
  const invoiceRowRefCallback = useCallback(
    (rowId: string) => (el: HTMLTableRowElement | null) => {
      if (el) {
        invoiceRowElements.current.set(rowId, el)
      } else {
        invoiceRowElements.current.delete(rowId)
      }
    },
    [],
  )

  const poRowRefCallback = useCallback(
    (rowId: string) => (el: HTMLTableRowElement | null) => {
      if (el) {
        poRowElements.current.set(rowId, el)
      } else {
        poRowElements.current.delete(rowId)
      }
    },
    [],
  )

  const relatedAnchorRefCallback = useCallback(
    (anchorId: string) => (el: HTMLElement | null) => {
      if (el) {
        relatedAnchorElements.current.set(anchorId, el)
      } else {
        relatedAnchorElements.current.delete(anchorId)
      }
    },
    [],
  )

  // Position recalculation
  const recalcPositions = useCallback(() => {
    const containerRect = connectorRef.current?.getBoundingClientRect()
    if (!containerRect) return

    const newInvoicePositions = calcPositions(
      invoiceRowElements.current,
      containerRect,
    )
    setInvoiceRowPositions((prev) =>
      positionsEqual(prev, newInvoicePositions) ? prev : newInvoicePositions,
    )

    const newPoPositions = calcPositions(poRowElements.current, containerRect)
    setPoRowPositions((prev) =>
      positionsEqual(prev, newPoPositions) ? prev : newPoPositions,
    )

    const newRelatedPositions = calcPositions(
      relatedAnchorElements.current,
      containerRect,
    )
    setRelatedAnchorPositions((prev) =>
      positionsEqual(prev, newRelatedPositions) ? prev : newRelatedPositions,
    )
  }, [])

  // Scroll listeners
  useEffect(() => {
    const invoiceEl = invoiceScrollRef.current
    const poEl = poScrollRef.current
    let rafId: number | null = null

    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(recalcPositions)
    }

    invoiceEl?.addEventListener('scroll', handleScroll, { passive: true })
    poEl?.addEventListener('scroll', handleScroll, { passive: true })

    // Initial calc after mount
    requestAnimationFrame(recalcPositions)

    return () => {
      invoiceEl?.removeEventListener('scroll', handleScroll)
      poEl?.removeEventListener('scroll', handleScroll)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [recalcPositions])

  // Recalc on container resize, and while the invoice pane's content changes
  // height (it fires every frame of an expand/collapse animation)
  useEffect(() => {
    const container = connectorRef.current
    if (!container) return
    const observer = new ResizeObserver(() => {
      recalcPositions()
    })
    observer.observe(container)
    if (invoiceContentRef.current) observer.observe(invoiceContentRef.current)
    return () => observer.disconnect()
  }, [recalcPositions])

  return {
    invoiceScrollRef,
    poScrollRef,
    connectorRef,
    invoiceContentRef,
    invoiceRowPositions,
    poRowPositions,
    relatedAnchorPositions,
    invoiceRowRefCallback,
    poRowRefCallback,
    relatedAnchorRefCallback,
    recalcPositions,
  }
}
